import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, GridItem, Typography, Heading } from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';

import { PageHeading } from 'components';
import {
  DfiReportFormattedMetrics,
  DfiReportResponse,
  KpiCategory,
  KpiLateResponse,
  ValueLabelResponse,
} from 'types';
import { StaticReviewCard, KpiTableCard, StaticKpiValueCard } from '../../components';
import { useFormattedDfiReport } from '../../hooks';
import { useFormatLocaleDate } from 'config/DateFormats/useFormatLocaleDate';

interface SaticExportDfiReportProps {
  heading: string;
  report: DfiReportResponse;
}

export const StaticExportDfiReport: React.FC<SaticExportDfiReportProps> = ({
  heading,
  report,
}) => {
  const { t } = useTranslation('annual_dfi_report_complete');

  const { report: dfiReport, titles, metrics: kpiData } = useFormattedDfiReport(report);

  const pageBreakCards: (keyof DfiReportFormattedMetrics)[] = [
    'lateResponseStatItems',
    'agreedExtensionStats',
    'totalEiaConsultationsReceived',
    'statutoryTimeframeExceededStats',
  ];

  const formatLocaleDate = useFormatLocaleDate();
  if (kpiData !== undefined) {
    return (
      <>
        <header>
          <PageHeading heading={heading} />
        </header>
        <section className='kpi-cards'>
          {Object.entries(kpiData).map(([key, value]) => (
            <div
              key={key}
              className={
                pageBreakCards.includes(key as keyof DfiReportFormattedMetrics)
                  ? 'kpi-card page-break-before'
                  : 'kpi-card'
              }
            >
              <Typography flex='1' textAlign='left' className='kpi-card-header'>
                <tqc.h3 marginBottom={0}>{titles[key]}</tqc.h3>
              </Typography>
              <div className='kpi-card-content'>
                {(key as KpiCategory) === KpiCategory.lateResponseStatItems ? (
                  <KpiTableCard lateResponses={value as KpiLateResponse[]} />
                ) : (
                  <StaticKpiValueCard kpiValues={value as ValueLabelResponse[]} />
                )}
              </div>
            </div>
          ))}
        </section>
        <section className='review-section'>
          <Heading size='lg'>{t('review', 'Review')}</Heading>
          <Typography>
            <tqc.p>
              {t(
                'review_sub_heading_text',
                'Consultees can use the report to highlight to the Department any initiatives or practices that may improve the consultation process. This may be a consultee committing to providing guidance or standing advice for use by planning officers that will negate the need for a consultation or it may propose an approach to dealing with consultations within its own organisation in a more efficient manner.'
              )}
            </tqc.p>
          </Typography>
          <Grid className='review-cards'>
            <GridItem>
              <StaticReviewCard
                question={t(
                  'dfi_review_question_1',
                  'Please highlight below any initiatives or best practices you have developed which have improved your consultation process and could be shared with other consultees to make the consultation process more streamlined for all.'
                )}
                response={dfiReport?.bestPracticeResponse}
              />
            </GridItem>
            <GridItem>
              <StaticReviewCard
                question={t(
                  'dfi_review_question_2',
                  'Please highlight any issues that you have had with the consultation process which you feel have hampered your ability to respond to consultation requests.'
                )}
                response={dfiReport?.infoOnLateResponse}
              />
            </GridItem>
          </Grid>
        </section>
        <Divider />
        <footer>
          <Typography>
            <tqc.p className='foot-note'>{`${t(
              'report_completed',
              'Report completed'
            )}: ${formatLocaleDate(new Date())}`}</tqc.p>
          </Typography>
        </footer>
      </>
    );
  } else {
    return null;
  }
};
